import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authGuard, superAdminGuard } from './guards'

// Layouts
import AuthLayout from '@/layouts/AuthLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'

const routes: Array<RouteRecordRaw> = [
  // Landing page (public, no auth)
  {
    path: '/landing',
    name: 'landing',
    component: () => import('@/views/LandingPage.vue'),
    meta: { title: 'Torcaza Projects — Agente como Servicio' },
  },

  // Cambio forzado de la clave temporal (usuario autenticado con mustChangePassword).
  {
    path: '/force-change-password',
    name: 'force-change-password',
    component: () => import('@/views/Auth/ForceChangePasswordPage.vue'),
    meta: { requiresAuth: true, title: 'Cambiar contraseña' },
  },

  // Auth routes (guest only)
  {
    path: '/',
    component: AuthLayout,
    meta: { requiresGuest: true },
    children: [
      {
        path: '',
        redirect: '/login',
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/Auth/LoginPage.vue'),
        meta: { title: 'Login' },
      },
      {
        path: 'register',
        redirect: '/login',
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/Auth/ForgotPasswordPage.vue'),
        meta: { title: 'Forgot Password' },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/views/Auth/ResetPasswordPage.vue'),
        meta: { title: 'Reset Password' },
      },
    ],
  },

  // Dashboard routes (requires auth)
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: 'Dashboard' },
      },
    ],
  },

  // CRM routes (admin only)
  {
    path: '/crm',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        redirect: '/crm/pipeline',
      },
      {
        path: 'pipeline',
        name: 'crm-pipeline',
        component: () => import('@/views/CRM/Pipeline/PipelinePage.vue'),
        meta: { title: 'Pipeline' },
      },
      {
        path: 'prospects',
        name: 'crm-prospects',
        component: () => import('@/views/CRM/Prospects/ProspectsPage.vue'),
        meta: { title: 'Prospectos' },
      },
      {
        path: 'prospects/:id',
        name: 'crm-prospect-detail',
        component: () => import('@/views/CRM/Prospects/ProspectDetailPage.vue'),
        meta: { title: 'Detalle de Prospecto' },
      },
    ],
  },

  // Ignite Program routes (admin only)
  {
    path: '/ignite',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        name: 'ignite-applications',
        component: () => import('@/views/Ignite/IgniteApplicationsPage.vue'),
        meta: { title: 'Postulaciones Ignite' },
      },
      {
        path: ':id',
        name: 'ignite-application-detail',
        component: () => import('@/views/Ignite/IgniteApplicationDetailPage.vue'),
        meta: { title: 'Detalle de Postulación' },
      },
    ],
  },

  // Organizations routes
  {
    path: '/organizations',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'operator'] },
    children: [
      {
        path: '',
        name: 'organizations',
        component: () => import('@/views/Organizations/OrganizationsPage.vue'),
        meta: { title: 'Organizaciones' },
      },
      {
        path: ':id',
        name: 'organization-detail',
        component: () => import('@/views/Organizations/OrganizationDetailPage.vue'),
        meta: { title: 'Detalle de Organizacion' },
      },
    ],
  },

  // My Organization (client only)
  {
    path: '/my-organization',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['client', 'member'] },
    children: [
      {
        path: '',
        name: 'my-organization',
        component: () => import('@/views/MyOrganization/MyOrganizationPage.vue'),
        meta: { title: 'Mi Organizacion' },
      },
    ],
  },

  // Projects routes
  {
    path: '/projects',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'projects',
        component: () => import('@/views/Projects/ProjectsPage.vue'),
        meta: { title: 'Proyectos' },
      },
      {
        path: ':id',
        name: 'project-detail',
        component: () => import('@/views/Projects/ProjectDetailPage.vue'),
        meta: { title: 'Detalle de Proyecto' },
      },
    ],
  },

  // Application detail (nested under projects)
  {
    path: '/projects/:id/applications/:applicationId',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'application-detail',
        component: () => import('@/views/Applications/ApplicationDetailPage.vue'),
        meta: { title: 'Detalle de Aplicacion' },
      },
    ],
  },

  // Deliverable detail (nested under projects)
  {
    path: '/projects/:id/deliverables/:deliverableId',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'deliverable-detail',
        component: () => import('@/views/Deliverables/DeliverableDetailPage.vue'),
        meta: { title: 'Detalle de Entregable' },
      },
    ],
  },

  // Meeting detail (nested under projects)
  {
    path: '/projects/:id/meetings/:meetingId',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'meeting-detail',
        component: () => import('@/views/Meetings/MeetingDetailPage.vue'),
        meta: { title: 'Detalle de Reunion' },
      },
    ],
  },

  // Requirements routes
  {
    path: '/requirements',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'requirements',
        component: () => import('@/views/Requirements/RequirementsPage.vue'),
        meta: { title: 'Requerimientos' },
      },
      {
        path: 'board',
        name: 'requirements-board',
        redirect: { name: 'sprint-board' },
      },
      {
        path: ':id',
        name: 'requirement-detail',
        component: () => import('@/views/Requirements/RequirementDetailPage.vue'),
        meta: { title: 'Detalle de Requerimiento' },
      },
    ],
  },

  // Backlog routes (admin/operator)
  {
    path: '/backlog',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'operator'] },
    children: [
      {
        path: '',
        name: 'backlog',
        component: () => import('@/views/Backlog/BacklogPage.vue'),
        meta: { title: 'Backlog' },
      },
      {
        path: 'board',
        name: 'sprint-board',
        component: () => import('@/views/Backlog/SprintBoardPage.vue'),
        meta: { title: 'Sprint Board' },
      },
    ],
  },

  // Petitions routes (client requests)
  {
    path: '/petitions',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'petitions',
        component: () => import('@/views/Petitions/PetitionsPage.vue'),
        meta: { title: 'Peticiones' },
      },
      {
        path: ':id',
        name: 'petition-detail',
        component: () => import('@/views/Petitions/PetitionDetailPage.vue'),
        meta: { title: 'Detalle de Peticion' },
      },
    ],
  },

  // Releases routes
  {
    path: '/releases',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'operator'] },
    children: [
      {
        path: '',
        name: 'releases',
        component: () => import('@/views/Releases/ReleasesPage.vue'),
        meta: { title: 'Releases' },
      },
      {
        path: ':id',
        name: 'release-detail',
        component: () => import('@/views/Releases/ReleaseDetailPage.vue'),
        meta: { title: 'Detalle de Release' },
      },
    ],
  },

  // Tickets routes
  {
    path: '/tickets',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'tickets',
        component: () => import('@/views/Tickets/TicketsPage.vue'),
        meta: { title: 'Tickets' },
      },
      {
        path: ':id',
        name: 'ticket-detail',
        component: () => import('@/views/Tickets/TicketDetailPage.vue'),
        meta: { title: 'Detalle de Ticket' },
      },
    ],
  },

  // Quotations routes
  {
    path: '/quotations',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'client', 'member'] },
    children: [
      {
        path: '',
        name: 'quotations',
        component: () => import('@/views/Quotations/QuotationsPage.vue'),
        meta: { title: 'Cotizaciones' },
      },
      {
        path: ':id',
        name: 'quotation-detail',
        component: () => import('@/views/Quotations/QuotationDetailPage.vue'),
        meta: { title: 'Detalle de Cotizacion' },
      },
    ],
  },

  // Sales Orders routes (admin only)
  {
    path: '/sales-orders',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        name: 'sales-orders',
        component: () => import('@/views/SalesOrders/SalesOrdersPage.vue'),
        meta: { title: 'Ordenes de Venta' },
      },
      {
        path: ':id',
        name: 'sales-order-detail',
        component: () => import('@/views/SalesOrders/SalesOrderDetailPage.vue'),
        meta: { title: 'Detalle de Orden' },
      },
    ],
  },

  // Applications routes (global listing)
  {
    path: '/applications',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'applications',
        component: () => import('@/views/Applications/ApplicationsPage.vue'),
        meta: { title: 'Aplicaciones' },
      },
    ],
  },

  // Deliverables routes (global listing)
  {
    path: '/deliverables',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'deliverables',
        component: () => import('@/views/Deliverables/DeliverablesPage.vue'),
        meta: { title: 'Entregables' },
      },
    ],
  },

  // Calendar routes
  {
    path: '/calendar',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'calendar',
        component: () => import('@/views/Calendar/CalendarPage.vue'),
        meta: { title: 'Calendario' },
      },
    ],
  },
  // Legacy redirect
  {
    path: '/appointments',
    redirect: '/calendar',
  },

  // Users routes (admin only)
  {
    path: '/users',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        name: 'users',
        component: () => import('@/views/Users/UsersPage.vue'),
        meta: { title: 'Usuarios' },
      },
    ],
  },

  // Activity Log (admin only)
  {
    path: '/activity-log',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        name: 'activity-log',
        component: () => import('@/views/ActivityLog/ActivityLogPage.vue'),
        meta: { title: 'Historial de Actividad' },
      },
    ],
  },

  // Tasks
  {
    path: '/tasks',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'operator', 'superadmin'] },
    children: [
      {
        path: '',
        name: 'tasks',
        component: () => import('@/views/Tasks/TaskBoardsPage.vue'),
        meta: { title: 'Tableros' },
      },
      {
        path: 'board/:boardId',
        name: 'task-board',
        component: () => import('@/views/Tasks/TaskBoardPage.vue'),
        meta: { title: 'Tablero' },
      },
      {
        path: ':id',
        name: 'task-detail',
        component: () => import('@/views/Tasks/TaskDetailPage.vue'),
        meta: { title: 'Detalle de Tarea' },
      },
    ],
  },

  // Integrations
  {
    path: '/integrations',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin', 'operator', 'superadmin'] },
    children: [
      {
        path: '',
        name: 'integrations',
        component: () => import('@/views/Integrations/IntegrationsPage.vue'),
        meta: { title: 'Integraciones' },
      },
    ],
  },

  // Trello OAuth Callback (no layout, standalone page)
  {
    path: '/integrations/trello/callback',
    name: 'trello-callback',
    component: () => import('@/views/Integrations/TrelloCallbackPage.vue'),
    meta: { requiresAuth: true, title: 'Conectando Trello' },
  },

  // Settings (admin only)
  {
    path: '/settings',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'] },
    children: [
      {
        path: '',
        name: 'settings',
        component: () => import('@/views/Settings/SettingsPage.vue'),
        meta: { title: 'Configuracion' },
      },
    ],
  },

  // Leads (superadmin only)
  {
    path: '/leads',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['superadmin'] },
    children: [
      {
        path: '',
        name: 'leads',
        component: () => import('@/views/Leads/LeadsPage.vue'),
        meta: { title: 'Leads' },
      },
    ],
  },

  // Profile (all authenticated users)
  {
    path: '/profile',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'profile',
        component: () => import('@/views/Profile/ProfilePage.vue'),
        meta: { title: 'Perfil' },
      },
    ],
  },

  // Notifications
  {
    path: '/notifications',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'notifications',
        component: () => import('@/views/Notifications/NotificationsPage.vue'),
        meta: { title: 'Notificaciones' },
      },
    ],
  },

  // Legal pages (public, no auth required)
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/Legal/TermsPage.vue'),
    meta: { title: 'Términos y Condiciones' },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/Legal/PrivacyPage.vue'),
    meta: { title: 'Política de Privacidad' },
  },

  // Public document view (no auth required)
  {
    path: '/public/doc/:token',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'public-document',
        component: () => import('@/views/Documents/PublicDocumentPage.vue'),
        meta: { title: 'Documento' },
      },
    ],
  },

  // SuperAdmin routes
  {
    path: '/super-admin',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    beforeEnter: superAdminGuard,
    children: [
      {
        path: '',
        name: 'super-admin-dashboard',
        component: () => import('@/views/SuperAdmin/SuperAdminDashboardPage.vue'),
        meta: { title: 'SuperAdmin' },
      },
      {
        path: 'tenants',
        name: 'super-admin-tenants',
        component: () => import('@/views/SuperAdmin/TenantsListPage.vue'),
        meta: { title: 'Tenants' },
      },
      {
        path: 'tenants/:id',
        name: 'super-admin-tenant-detail',
        component: () => import('@/views/SuperAdmin/TenantDetailPage.vue'),
        meta: { title: 'Detalle de Tenant' },
      },
      {
        path: 'feedback',
        name: 'super-admin-feedback',
        component: () => import('@/views/SuperAdmin/FeedbackPage.vue'),
        meta: { title: 'Feedback' },
      },
      {
        path: 'sessions',
        name: 'super-admin-sessions',
        component: () => import('@/views/Sessions/SessionsPage.vue'),
        meta: { title: 'Sesiones de terminal' },
      },
      {
        path: 'agent-sessions',
        name: 'super-admin-agent-sessions',
        component: () => import('@/views/Sessions/AgentSessionsPage.vue'),
        meta: { title: 'Sesiones de agente' },
      },
      {
        path: 'knowledge-graph',
        name: 'super-admin-knowledge-graph',
        component: () => import('@/views/SuperAdmin/KnowledgeGraphPage.vue'),
        meta: { title: 'Grafo de conocimiento' },
      },
      {
        path: 'blog',
        name: 'super-admin-blog',
        component: () => import('@/views/Blog/BlogPostsPage.vue'),
        meta: { title: 'Blog' },
      },
      {
        path: 'blog/new',
        name: 'super-admin-blog-new',
        component: () => import('@/views/Blog/BlogPostEditorPage.vue'),
        meta: { title: 'Nuevo Post' },
      },
      {
        path: 'blog/:id/edit',
        name: 'super-admin-blog-edit',
        component: () => import('@/views/Blog/BlogPostEditorPage.vue'),
        meta: { title: 'Editar Post' },
      },
    ],
  },

  // Dev Center routes — herramientas internas de desarrollo, gateadas por el
  // módulo `devCenter` del tenant + rol admin. El superadmin siempre entra
  // (además las tiene bajo /super-admin). Espeja el grupo "Dev Center" del mobile.
  {
    path: '/dev-center',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['admin'], requiresModule: 'devCenter' },
    children: [
      {
        path: 'knowledge-graph',
        name: 'dev-center-knowledge-graph',
        component: () => import('@/views/SuperAdmin/KnowledgeGraphPage.vue'),
        meta: { title: 'Grafo de conocimiento' },
      },
      {
        path: 'codegraph',
        name: 'dev-center-codegraph',
        component: () => import('@/views/DevCenter/CodeGraphPage.vue'),
        meta: { title: 'Grafo de código' },
      },
      {
        path: 'sessions',
        name: 'dev-center-sessions',
        component: () => import('@/views/Sessions/SessionsPage.vue'),
        meta: { title: 'Sesiones de terminal' },
      },
      {
        path: 'agent-sessions',
        name: 'dev-center-agent-sessions',
        component: () => import('@/views/Sessions/AgentSessionsPage.vue'),
        meta: { title: 'Sesiones de agente' },
      },
      {
        path: 'pipelines',
        name: 'dev-center-pipelines',
        component: () => import('@/views/DevCenter/PipelinesPage.vue'),
        meta: { title: 'Pipelines' },
      },
    ],
  },

  // Implementations routes
  {
    path: '/implementations',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['superadmin', 'admin', 'operator'] },
    children: [
      {
        path: '',
        name: 'Implementations',
        component: () => import('@/views/Implementations/ImplementationsPage.vue'),
        meta: { title: 'Implementaciones' },
      },
      {
        path: ':id',
        name: 'implementation-detail',
        component: () => import('@/views/Implementations/ImplementationDetailPage.vue'),
        meta: { title: 'Detalle de Implementacion' },
      },
    ],
  },

  // Unauthorized
  {
    path: '/unauthorized',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'unauthorized',
        component: () => import('@/views/UnauthorizedPage.vue'),
        meta: { title: 'No Autorizado' },
      },
    ],
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'not-found',
        component: () => import('@/views/NotFoundPage.vue'),
        meta: { title: 'No Encontrado' },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

// Global navigation guard
router.beforeEach(authGuard)

// Telemetry: track page views
import { trackPageView } from '@/services/telemetry'
router.afterEach((to) => {
  trackPageView(to.path)
})

export default router
